/**
 * 문서 삭제 권한 판정 — **한 벌**(2026-09-09).
 *
 * ## 왜 이 파일이 생겼나
 *
 * 같은 질문("이 사람이 이 문서를 지울 수 있나")에 **세 자리가 답하고 있었고 둘은 다른 축**이었다.
 *
 * | 자리 | 축 |
 * |---|---|
 * | 서버 `ReportDeleteService.delete`/`requestDelete` | `status` + actor + **제3자 결재 서명 유무**. type을 **전혀 묻지 않는다** |
 * | 상세 `useReportPermissions` | 서버와 같은 축. type을 묻지 않는다 |
 * | 목록 `pages/reports/index.tsx` | `type` + `status` — **서버보다 엄격**하고, 그 과잉 제한을 `type === 'TBM'` 특례가 되풀어 줬다 |
 *
 * 목록이 엄격했던 자리는 `PENDING_APPROVAL`/`APPROVED`를 **Owner 전용**으로 본 것이다. 서버는
 * 그 두 상태에서도 작성자를 허용한다(제3자 결재가 붙었으면 삭제 «요청» 경로로). 그래서 목록의
 * TBM 특례는 「TBM은 예외」가 아니라 **자기가 만든 과잉 제한을 자기가 푼 것**이었고, 축을
 * 서버·상세와 맞추면 특례는 넓힐 것도 없이 **사라진다**.
 *
 * ## 반환이 왜 「문구」가 아니라 「코드」인가
 *
 * 두 surface의 문구가 다르다(목록 "담당 현장의 **문서**만…" / 상세 "담당 현장의 **보고서**만…").
 * 문구를 이 파일이 소유하면 둘 중 하나의 화면 어휘가 조용히 바뀐다. 판정만 여기서 하고 말은
 * 각 surface가 한다 — 축이 갈리는 것은 막고 어휘는 건드리지 않는다.
 *
 * ## type을 묻지 않는다
 *
 * 입력에 `type`이 **없는 것이 계약**이다. 구 TBM(`type === 'TBM'`)을 Composer로 전환하는 중이라
 * 정체 축은 어차피 낡는데, 삭제 권한은 애초에 정체가 아니라 **결재 상태**의 질문이었다
 * (결정 `2026-09-08-tbm-status-model-two-axes.md` [업데이트] §(A) — "원 판단 근거를 결정 문서에서
 * 못 찾았다"가 그 자리 그대로다).
 */

/** 삭제 판정 결과 — 문구는 담지 않는다(위 doc "코드" 절). */
export type DeleteDecision =
  /** 담당 현장이 아니다 — 볼 수는 있어도 지울 수 없다. */
  | { kind: 'site-not-assigned' }
  /**
   * 삭제 권한(`report.delete`)도 없고 작성자도 아니다.
   *
   * 종전 이름은 `not-owner-or-author`였다 — 판정이 `role === 'OWNER'`이던 시절의 어휘다.
   * P6(조직 RBAC 판정 이관)부터 역할이 아니라 권한 key를 보므로 이름도 그 축으로 옮겼다.
   */
  | { kind: 'no-delete-permission' }
  /**
   * 지울 수 있다. `requiresApproval`이면 **직접 삭제가 아니라 삭제 승인 요청** 경로다
   * (서버 `delete()`는 그 경우 `REPORT_DELETE_NOT_ALLOWED`를 던지고 `requestDelete()`만 받는다).
   */
  | { kind: 'allowed'; requiresApproval: boolean };

export interface DeletePermissionInput {
  /**
   * 삭제 **자격** — 권한 key `report.delete`(조직 RBAC, P6 판정 이관 ③).
   *
   * 서버 `ReportDeleteService.delete`가 세 상태 분기에서 똑같이 묻는 `!isOwner && !isAuthor`의
   * 앞항이다(:308·:318·:329 — 정의 `permission.definitions.ts` `report.delete` 「대체 대상」).
   * 뒷항(작성자)은 **신원 축**이라 키로 바뀌지 않는다.
   *
   * ⚠️ **여기에 현장 항을 다시 걸지 말 것.** 사용자 결정 2026-09-24 「② 형상」은
   * `작성자 ∥ (배정 현장 && 키)`인데, 아래 `resolveDeleteDecision`이 `siteAssigned` 불충족을
   * **선행 early-return**으로 치므로 이 필드에 도달한 시점엔 이미 배정 현장이다. 호출부에서
   * `siteAssigned && can(...)`로 또 묶으면 같은 조건이 두 곳에 산다.
   */
  canDelete: boolean;
  /**
   * 제3자 결재 서명이 있어도 **승인 절차 없이** 지울 수 있는가 — 권한 key `report.delete-approved`
   * (`orgAdjustable=false`. 결재 완료본은 법적 증빙이라 조직이 넓힐 수 없다).
   *
   * 이 축이 사는 곳은 **둘**이다:
   * - `PENDING_APPROVAL` — 제3자 서명이 있어도 즉시 삭제(서버 PENDING 분기 「Owner는 서명 유무 무관」).
   * - `APPROVED` + 제3자 서명 **0건** — 직접 삭제의 자격 그 자체다(2026-09-25 S43에서 `report.delete`에서
   *   이 키로 좁혔다). 제3자 서명이 1건+면 서버가 actor를 묻지 않고 거부하므로 그때는 누구든 승인 요청 경로다.
   */
  canDeleteApproved: boolean;
  isAuthor: boolean;
  /** 문서 상태. `ReportStatus`로 좁히지 않는 이유는 목록·상세가 서로 다른 형상을 들고 있어서다. */
  status: string;
  /**
   * 담당 현장인가. 「현장이 없다」도 여기서는 **true**(제약 없음)로 넘긴다 —
   * 현장 유무 판정은 호출부가 이미 하고 있고, 그 술어(`isSiteAssigned`)는 멤버십을 요구한다.
   */
  siteAssigned: boolean;
  /**
   * **최신 라운드에 제3자 결재 승인 서명이 있는가** — `status`와 무관한 «문서 사실»이다.
   *
   * ⚠️ 「승인이 필요한가」가 **아니다.** 필요 여부는 actor에 달렸다(PENDING_APPROVAL에서 Owner는
   * 서명 유무와 무관하게 즉시 삭제한다 — 서버 `delete()` "Owner는 서명 유무 무관"). 그래서 서버
   * 목록 응답도 이 사실만 스칼라로 주고(`hasThirdPartyApproval`), 조합은 여기서 한다.
   *
   * 제3자 판정 자체(author 단계·미지정 칸·작성자 본인 서명 제외)는 공용
   * `createThirdPartyApprovalCheck`가 소유한다 — 상세는 결재란 정의를 들고 있어 직접 계산하고,
   * 목록은 그 정의를 응답에서 벗기므로 서버가 세어 준다. 두 경로가 **같은 유틸**을 쓴다.
   */
  hasThirdPartyApproval: boolean;
}

/**
 * 서버 `ReportDeleteService`(delete·requestDelete)와 **같은 축**의 판정.
 *
 * | 상태 | `report.delete-approved` 보유 | 그 외(자격 `report.delete` 또는 작성자) |
 * |---|---|---|
 * | `PENDING_APPROVAL` | 즉시 삭제 | 제3자 결재 0건이면 즉시 삭제 / 1건+면 승인 요청 |
 * | `APPROVED` + 제3자 결재 1건+ | 승인 요청 | 승인 요청(자격 `report.delete` ∥ 작성자) |
 * | `APPROVED` + 제3자 결재 0건 | 즉시 삭제 | **작성자만** 즉시 삭제 — 그 외는 지울 수 없다 |
 * | 그 외(`CREATING`·`DRAFT`·`REJECTED`) | 즉시 삭제 | 즉시 삭제 |
 *
 * ⚠️ 마지막 행이 2026-09-25에 좁혀졌다(S43). 종이(담당자 미지정) 결재로 확정된 문서는 제3자 서명이 0이라
 * 직접 삭제로 떨어지는데, 그 키가 `report.delete`면 **조직이 켜 준 사람이 결재 완료본을 되돌릴 수 없게
 * 지운다**(dev 실측에서 실제로 지워졌다). 서버가 잠긴 키를 요구하도록 바뀌었고 이 판정이 그 거울이다.
 */
export function resolveDeleteDecision(input: DeletePermissionInput): DeleteDecision {
  if (!input.siteAssigned) return { kind: 'site-not-assigned' };

  if (input.status === 'APPROVED') {
    // 확정본은 **자격 축이 다르다**(2026-09-25 S43) — 그래서 아래 공통 게이트보다 먼저 답한다.
    if (input.hasThirdPartyApproval) {
      // 제3자 결재가 있으면 누구든 승인 요청 경로다. 요청 «자격»은 서버 `requestDelete`와 같이
      // `report.delete` ∥ 작성자다(잠긴 키는 여기서 아무것도 더 열지 않는다).
      return input.canDelete || input.isAuthor
        ? { kind: 'allowed', requiresApproval: true }
        : { kind: 'no-delete-permission' };
    }
    // 제3자 결재가 없는 확정본(종이 결재·작성자 서명만)은 **직접 삭제**인데, 그 키는 조직이 켤 수 있는
    // `report.delete`가 아니라 **잠긴 키** `report.delete-approved`다 — 서버 delete() APPROVED 분기 동형.
    // 승인 요청 경로도 「승인해 줄 사람이 없다」로 거부되므로 여기서 막지 않으면 눌리는데 실패하는 버튼이 된다.
    return input.canDeleteApproved || input.isAuthor
      ? { kind: 'allowed', requiresApproval: false }
      : { kind: 'no-delete-permission' };
  }

  if (!input.canDelete && !input.isAuthor) return { kind: 'no-delete-permission' };

  if (input.status === 'PENDING_APPROVAL') {
    // `report.delete-approved`는 서명 유무 무관 즉시 삭제 (서버 delete() PENDING 분기 동형).
    if (input.canDeleteApproved) return { kind: 'allowed', requiresApproval: false };
    return { kind: 'allowed', requiresApproval: input.hasThirdPartyApproval };
  }

  return { kind: 'allowed', requiresApproval: false };
}
